import {
	unref as vueUnref,
	toRaw as vueToRaw,
	isRef as vueIsRef,
	isReactive as vueIsReactive,
	isProxy as vueIsProxy
} from 'vue';

import isEqual 				from "lodash.isequal";
import cloneDeepWith from 'lodash.clonedeepwith';

import StringUtils 		from '@/core/utils/StringUtils';
import NumberUtils 		from '@/core/utils/NumberUtils';
import DateUtils 			from '@/core/utils/DateUtils';
import RegionalUtils 	from '@/core/utils/RegionalUtils';
import MoneyUtils 		from '@/core/utils/MoneyUtils';
import FileUtils 			from "@/core/utils/FileUtils";
import MaskUtils 			from "@/core/utils/MaskUtils";
import ValidatorUtils from "@/core/utils/ValidatorUtils";
import DomUtils 			from "@/core/utils/DomUtils";

import ClipboardXlsParserUtils from '@/core/utils/ClipboardXlsParserUtils';

/**
 * Утилиты приложения (Utilities Namespace)
 *
 * Реализует Namespace Pattern для организации утилитарных функций:
 * - Группирует связанные утилиты по категориям (string, number, date, etc.)
 * - Предоставляет единое пространство имен для всех helper-функций
 * - Избегает загрязнения глобального пространства имен
 *
 * Использование: Utils.string.capitalize(), Utils.date.format(), etc.
 */

class Utils {

	static get string() {
		return StringUtils;
	}

	static get number() {
		return NumberUtils;
	}

	static get date() {
		return DateUtils;
	}

	static get regional() {
		return RegionalUtils;
	}

	static get money() {
		return MoneyUtils;
	}

	static get file() {
		return FileUtils;
	}

	static get validators() {
		return ValidatorUtils;
	}

	static get mask() {
		return MaskUtils;
	}

	static get dom() {
		return DomUtils;
	}

	/**
	 * Класс для парсинга HTML-таблиц из буфера обмена (вставка из Excel / Google Sheets).
	 * Используется через new: const parser = new Utils.ClipboardXlsParser(event)
	 *
	 * ps: static readonly вместо геттера - потому что это ссылка на конструктор класса
	 */
	static readonly ClipboardXlsParser = ClipboardXlsParserUtils;

	// Vue reactivity helpers
	static unref = vueUnref;
	static toRaw = vueToRaw;
	static isRef = vueIsRef;
	static isReactive = vueIsReactive;
	static isProxy = vueIsProxy;

	/**
	 * Проверяет, является ли значение объектом
	 */
	static isObject(value: unknown): value is Record<string, any> {
		value = vueUnref(value);
		return value !== null && typeof value === 'object';
	}

	/**
	 * Проверяем на равенство на равенство, а не ссылки
	 * */

	static isEqual = isEqual;

	static isFunction(value: unknown): value is Function {
		value = vueUnref(value);
		return typeof value === 'function';
	}

	/**
	 * Почему нельзя использовать lodash.cloneDeep напрямую?
	 *
	 * Lodash перебирает объект и при встрече пользовательских типов вызывает их конструкторы.
	 * Классы с особой логикой в конструкторе (например, TimeStamp с применением timezone offset)
	 * будут склонированы неправильно — офсет применится дважды.
	 *
	 * ============================================================================
	 * TL;DR: Как работает клонирование дат в lodash.cloneDeep
	 * ============================================================================
	 *
	 * function initCloneByTag(object, tag, cloneFunc, isDeep) {
	 *   var Ctor = object.constructor;  // Берёт конструктор
	 *   switch (tag) {
	 *     case dateTag:
	 *       return new Ctor(+object);   // ← Вызывает new TimeStamp(timestamp)
	 *   }
	 * }
	 *
	 * Проблема: +object — это timestamp УЖЕ с офсетом, а new TimeStamp() применяет офсет снова.
	 *
	 * ============================================================================
	 * Решение
	 * ============================================================================
	 *
	 * Используем обёртку с customizer, который проверяет наличие метода .cloneDeep()
	 * у каждого объекта перед клонированием.
	 *
	 * @param value - Значение для клонирования
	 * @returns Глубокая копия значения
	 *
	 * @example
	 * // Проблема с lodash напрямую:
	 * const original = new TimeStamp('2024-03-01'); // Офсет применён один раз
	 * const cloned = _.cloneDeep(original);          // Офсет применён второй раз! ❌
	 *
	 * @example
	 * // Правильное использование:
	 * const original = new TimeStamp('2024-03-01');
	 * const cloned = cloneDeep(original);            // Вызовет TimeStamp.cloneDeep() ✅
	 *
	 * @example
	 * // Работает с вложенными объектами:
	 * const obj = {
	 *   name: 'test',
	 *   date: new TimeStamp('2024-03-01'),  // Вызовет TimeStamp.cloneDeep()
	 *   nested: {
	 *     range: new DateRange(start, end),  // Вызовет DateRange.cloneDeep()
	 *     data: [1, 2, 3]                    // Lodash склонирует стандартно
	 *   }
	 * };
	 * const cloned = cloneDeep(obj);
	 */

	/**
	 * Кастомайзер для cloneDeepWith — обрабатывает типы с собственным cloneDeep.
	 *
	 * Выделен в отдельный метод, чтобы не дублировать логику
	 * между cloneDeep и cloneDeepWithReference.
	 *
	 * @returns Результат кастомного клонирования или undefined (= продолжить стандартное клонирование lodash)
	 */
	private static cloneCustomizer(val: any): any | undefined {
		// Перехватываем Date и его наследников ДО lodash
		if (val instanceof Date && typeof (val as any).cloneDeep === 'function') {
			return (val as any).cloneDeep();
		}
		// Для объектов с собственным cloneDeep
		if (val && typeof val === 'object' && typeof val.cloneDeep === 'function') {
			return val.cloneDeep();
		}
		return undefined;
	}

	static cloneDeep<T>(value: T): T {
		return cloneDeepWith(value, (val: any) => this.cloneCustomizer(val));
	}

	/**
	 * Глубокое клонирование с сохранением ссылок на указанные ключи.
	 *
	 * Решает проблему циклических ссылок между сущностями (пример: position/order):
	 * при обычном cloneDeep ссылка на родительский объект «замораживается»,
	 * и изменения в родителе не отражаются на клоне.
	 *
	 * @param value - Значение для клонирования
	 * @param referenceKeys - Ключи, значения которых копируются по ссылке без клонирования
	 *
	 * @example
	 * // position.order останется той же ссылкой, остальное склонируется
	 * const clone = Utils.cloneDeepWithReference(position, ['order']);
	 */
	static cloneDeepWithReference<T>(value: T, referenceKeys: string[] = []): T {
		const referenceKeysSet = new Set(referenceKeys);

		return cloneDeepWith(value, (val: any, key: string | number | undefined) => {
			// Сохраняем ссылку для указанных ключей
			if (typeof key === 'string' && referenceKeysSet.has(key)) {
				return val;
			}
			return this.cloneCustomizer(val);
		});
	}

	/**
	 * Проверяет, является ли массив пустым
	 */
	static isEmptyArray(arr: unknown): arr is [] {
		arr = vueUnref(arr);
		return Array.isArray(arr) && arr.length === 0;
	}

	static isNotEmptyArray<T = any>(arr: unknown): arr is T[] {
		arr = vueUnref(arr);
		return Array.isArray(arr) && arr.length > 0;
	}

	/**
	 * Проверка на null и undefined
	 */
	static isNullOrUndefined(value: unknown): value is null | undefined {
		value = vueUnref(value);
		return value === null || typeof value === "undefined";
	}

	/**
	 * Проверяет есть ли у объекта свойство. Если передан не объект, null или undefined - возвращает false
	 */
	static haveProperty(object: unknown, propertyName: string): boolean {
		object = vueUnref(object);
		return this.isObject(object) && propertyName in object;
	}

	/**
	 * Метод для копирования строки в буфер обмена
	 * @param text - Текст для копирования
	 * @returns true при успехе, false при ошибке
	 */
	static async copyToClipboard(text: string): Promise<boolean> {
		try {
			const el = document.createElement('textarea');
			el.value = text;
			el.setAttribute('readonly', '');
			el.style.position = 'absolute';
			el.style.left = '-9999px';

			document.body.appendChild(el);

			const selected = document.getSelection().rangeCount > 0
				? document.getSelection().getRangeAt(0)
				: false;
			el.select();

			if (!navigator.clipboard) {
				document.execCommand('copy');
			} else {
				await navigator.clipboard.writeText(text);
			}

			document.body.removeChild(el);

			if (selected) {
				document.getSelection().removeAllRanges();
				document.getSelection().addRange(selected);
			}
			return true;
		} catch (error) {
			console.error(error);
			return false;
		}
	}

	/**
	 * Безопасно получает значение вложенного свойства объекта по пути
	 * @param path - Путь к свойству в формате "a.b.c"
	 * @param source - Объект, из которого нужно получить значение
	 * @returns Значение свойства, если найдено, иначе null или undefined
	 */
	static accessNestedProperty(path: string, source: unknown): any {
		source = vueUnref(source);

		if (!this.isObject(source) || !(typeof path === "string" && path.length > 0)) {
			return undefined;
		}

		return path.split(".").reduce((result: any, pathItem: string) => {
			result = vueUnref(result);
			if (!(this.isObject(result) && pathItem in result)) return null;
			return result[pathItem];
		}, source);
	}

	static arrayToMap(array: any[]): Map<number, any> {
		const map = new Map();
		array?.forEach(item => {
			if (item?.id !== undefined) {
				map.set(item.id, item);
			}
		});
		return map;
	}

	/**
	 * Упрощает объект для отправки на сервер
	 *
	 * Преобразует сложные объекты к минимальному представлению для API:
	 * - Объекты с `id` сводятся к набору ключевых полей: { id, name?, value?, version? }
	 * - Убирает префикс подчёркивания у приватных полей (_field → field)
	 * - Рекурсивно обрабатывает вложенные объекты и массивы
	 * - Сохраняет даты без изменений
	 * - Примитивные значения возвращаются как есть
	 *
	 * @param {any} data - Данные для упрощения (объект, массив или примитив)
	 * @returns {any} Упрощённый объект, готовый для отправки на сервер
	 *
	 * @example
	 * // Упрощение объекта с ID
	 * const partner = {
	 *   id: 123,
	 *   name: 'ООО "Рога и копыта"',
	 *   companies: [{ id: 1, title: 'Компания 1' }],
	 *   contracts: [...]
	 * };
	 * simplifyObjectForServer(partner);
	 * // → { id: 123, name: 'ООО "Рога и копыта"' }
	 *
	 * @example
	 * // Обработка приватных полей
	 * const tender = {
	 *   id: 456,
	 *   title: 'Тендер на поставку',
	 *   _seller: { id: 789, name: 'Продавец', companies: [...] },
	 *   createdAt: new Date('2024-01-15')
	 * };
	 * simplifyObjectForServer(tender);
	 * // → {
	 * //   id: 456,
	 * //   title: 'Тендер на поставку',
	 * //   seller: { id: 789, name: 'Продавец' },
	 * //   createdAt: Date('2024-01-15')
	 * // }
	 *
	 * @example
	 * // Обработка массивов
	 * const bids = [
	 *   { id: 1, price: 1000, seller: { id: 10, name: 'Seller A', contacts: [...] } },
	 *   { id: 2, price: 900, seller: { id: 20, name: 'Seller B', contacts: [...] } }
	 * ];
	 * simplifyObjectForServer(bids);
	 * // → [
	 * //   { id: 1, price: 1000, seller: { id: 10, name: 'Seller A' } },
	 * //   { id: 2, price: 900, seller: { id: 20, name: 'Seller B' } }
	 * // ]
	 *
	 * @example
	 * // Объект без ID возвращается полностью (с обработкой вложенных полей)
	 * const config = {
	 *   theme: 'dark',
	 *   _settings: { timeout: 5000 },
	 *   partner: { id: 100, companies: [...] }
	 * };
	 * simplifyObjectForServer(config);
	 * // → {
	 * //   theme: 'dark',
	 * //   settings: { timeout: 5000 },
	 * //   partner: { id: 100 }
	 * // }
	 */
	static simplifyObjectForServer(data) {
		// Конвертируем undefined в null сразу
		if (data === undefined) { return null; }

		if (Array.isArray(data)) {
			return data.map(item => this.simplifyObjectForServer(item));
		}

		if (typeof data === 'object' && data !== null) {
			if (data?.id) {
				const result = { id: data.id };
				if (data?.name) result.name = data.name;
				if (data?.value !== undefined) { result.value = data.value; }
				/** Из-за характеристик добавил **/
				if (data?.valueType) result.valueType = data.valueType;
				if (data?.version) result.version = data.version;

				/** поддержка EntityPointer */
				if (data?.typeName) result.typeName = data.typeName;
				return result;
			}


			// Если у объекта нет поля "id", возвращаем его полностью
			const result = {};
			for (const key in data) {
				if (Object.prototype.hasOwnProperty.call(data, key)) {
					const innerKey = key.substring(0, 1) === '_' ? key.slice(1) : key;

					/** Если это дата */
					if (Utils.date.isDate(data[key])) {
						result[innerKey] = data[key];
					} else {
						result[innerKey] = this.simplifyObjectForServer(data[key]);
					}
				}
			}
			return result;
		}

		// Если это не объект и не массив, возвращаем как есть
		return data;
	}


	/** =================== Мне жалко выбрасывать эту функцию, я её писал сам ===================  **/
	// PS И она даже работает

	//
	// static removeCircle(obj) {
	// 	const seen = new WeakSet();
	//
	// 	function helper(value, depth = 0) {
	// 		if (typeof value === 'object' && value !== null) {
	// 			/** все цикликческие ссылки глубже LAST_IGNORED_NESTING_LEVEL уровня вложенности будут заменены */
	// 			if (depth > this.LAST_IGNORED_NESTING_LEVEL) {
	// 				if (seen.has(value)) {
	// 					/** индикатор */
	// 					return '[Circular]';
	// 				}
	// 				seen.add(value);
	// 			}
	//
	// 			if (Array.isArray(value)) {
	// 				return value.map(item => helper(item, depth + 1));
	// 			}
	//
	// 			if (Utils.date.isDate(value)) {
	// 				return value;
	// 			}
	//
	// 			const result = {};
	// 			for (const key in value) {
	// 				if (Object.prototype.hasOwnProperty.call(value, key)) {
	// 					/** хочется все приватные поля */
	// 					const innerKey   = key.substring(0, 1) === '_' ? key.slice(1) : key;
	// 					result[innerKey] = helper(value[key], depth + 1);
	// 				}
	// 			}
	// 			return result;
	// 		}
	// 		return value;
	// 	}
	//
	// 	return helper(obj);
	// }

  static removeCircular(obj: object, maxDepth: number = 5): Record<string, any> {
    const seen = new WeakSet();

    const helper = (value: any, depth: number): any => {
      if (typeof value !== 'object' || value === null) {
        return value;
      }

      if (Utils.date.isDate(value)) {
        return value;
      }

      if (seen.has(value)) {
        if (depth > maxDepth) {
          return '[Circular]';
        }
      }
      else {
        seen.add(value);
      }

      let result: any;

      if (Array.isArray(value)) {
        result = value.map(item => helper(item, depth + 1));
      }
      else {
        result = {} as Record<string, any>;

        for (const key in value) {
          if (Object.prototype.hasOwnProperty.call(value, key)) {
            const innerKey = key[0] === '_' ? key.slice(1) : key;
            result[innerKey] = helper(value[key], depth + 1);
          }
        }
      }

      seen.delete(value);

      return result;
    };

    return helper(obj, 0);
  }

	/**
	 * Копирует HTML-контент в буфер обмена с сохранением форматирования.
	 * При вставке в rich text редакторы (Word, Gmail, Google Docs) сохраняется
	 * оригинальное оформление: жирный шрифт, списки, отступы и т.д.
	 *
	 * @param html - HTML-строка для копирования
	 * @returns Promise<boolean> - true если копирование успешно, false в случае ошибки
	 */
	static async copyHtmlToClipboard(html: string): Promise<boolean> {
		try {
			if(!navigator.clipboard || !window.isSecureContext) {
				console.error('Clipboard API недоступен');
				return false;
			}
			navigator.clipboard && window.isSecureContext
				/**
				 * Blob — обязательная обёртка для Clipboard API с указанием MIME-типа 'text/html'
				 */
				const clipboardItem = new ClipboardItem({
					'text/html': new Blob([html], { type: 'text/html' }),
				});

				await navigator.clipboard.write([clipboardItem]);
				return true;
		} catch (error) {
			console.error(error);
			return false;
		}
	}

	/**
	 * Преобразует данные с сервера в объект через конструктор.
	 * Если данных нет (null/undefined) — возвращает null.
	 *
	 * Централизует проверку на null/undefined, чтобы не дублировать её
	 * по всему проекту и/или не забывать.
	 *
	 * @example
	 * partner.category = Utils.hydrate(data.category, PartnerCategory.fromServer);
	 * // вместо: Utils.isObject(data.category) ? PartnerCategory.fromServer(data.category) : null;
	 */
	static hydrate<R>(data: any, constructorCallback: (data: any) => R): R | null {
		return data != null ? constructorCallback(data) : null;
	}

	/**
	 * Преобразует массив данных с сервера в массив объектов через конструктор.
	 * Если данных нет (null/undefined) — возвращает пустой массив.
	 *
	 * Централизует проверку на null/undefined, чтобы не дублировать её
	 * по всему проекту и/или не забывать.
	 *
	 * @example
	 * partner.persons = Utils.hydrateArray(data.persons, Person.fromServer);
	 * // вместо: Utils.isNotEmptyArray(data.persons) ? data.persons.map((item: any) => Person.fromServer(item)) : [];
	 */
	static hydrateArray<R>(data: any, constructorCallback: (item: any) => R): R[] {
		return data != null ? data.map(item => constructorCallback(item)) : [];
	}

	/**
	 * Выполняет массив функций/промисов и возвращает Promise.all с результатами.
	 * Обрабатывает функции, промисы и обычные значения единообразно.
	 *
	 * @param listeners - Функция, промис, значение или массив из них
	 * @returns Promise с массивом результатов
	 *
	 * @example
	 * await Utils.invokeFunctions([*
	 *   () => console.log('sync'),
	 *   async () => await fetchData(),
	 *   somePromise
	 * *]);
	 */
	static invokeFunctions(listeners: unknown): Promise<unknown[]> {
		const functionsArray = Array.isArray(listeners) ? listeners : [listeners];

		const promises = functionsArray.map(func => {
			try {
				if (typeof func === 'function') {
					const result = func();
					return result instanceof Promise ? result : Promise.resolve(result);
				}
				if (func instanceof Promise) {
					return func;
				}
				return Promise.resolve(func);
			} catch (error) {
				return Promise.resolve();
			}
		});

		return Promise.all(promises);
	}
}

export default Utils;
